import React, { useEffect, useState } from 'react';

import { expValEquals } from '@confluence/feature-experiments';

declare global {
	interface Window {
		__SSR_RENDERED__?: boolean;
		__HYDRATABLE__?: boolean;
		__FULLCYCLE__?: Record<string, string | undefined | TrustedHTML>;
	}
}

export const CLEANUP_TIMEOUT = 30000;

const useTwoStageHydration = (name: string, enabled: boolean) => {
	const fullCycleOutput = window.__FULLCYCLE__?.[name];
	const isNoSSR = !window.__SSR_RENDERED__;

	// Rely on a normal hydration/rendering flow when it's a transition or there's no full cycle output
	// or when experience is not hydratable
	const [hydrated, setHydrated] = useState(
		isNoSSR || !fullCycleOutput || !window.__HYDRATABLE__ || !enabled,
	);

	useEffect(() => {
		setHydrated(true);

		const cleanup = () => {
			delete window.__FULLCYCLE__?.[name];
		};

		// Arbitrary 30s CLEANUP_TIMEOUT to allow for the element to be rendered before removal
		// to not force an unecessary re-render or flash of blank content, even in slowest cases
		setTimeout(cleanup, CLEANUP_TIMEOUT);

		return cleanup;
	}, [name]);

	return { hydrated, fullCycleOutput };
};

export const saveFullCycleOutput = () => {
	const nodesForPartialHydration = document.querySelectorAll(
		'[data-fullcycle-name]',
	) as NodeListOf<HTMLElement>;
	if (nodesForPartialHydration.length > 0) {
		window.__FULLCYCLE__ = Array.from(nodesForPartialHydration).reduce(
			(acc, node) => {
				acc[node.dataset.fullcycleName as string] = node.innerHTML as string | TrustedHTML;
				return acc;
			},
			{} as Record<string, string | undefined | TrustedHTML>,
		);
	}
};

export const TwoStageHydrationInner = ({
	style,
	name,
	children,
	enabled = false,
}: {
	style?: React.CSSProperties;
	name: string;
	children: React.ReactNode;
	enabled?: boolean;
}) => {
	const { hydrated, fullCycleOutput } = useTwoStageHydration(name, enabled);

	// NOTE: REACT_SSR is used intentionally here to allow for the two-stage hydration to work
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (enabled && process.env.REACT_SSR) {
		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={style}
				data-testid="two-stage-hydration"
				data-fullcycle-name={name}
			>
				{children}
			</div>
		);
	}

	if (!enabled || hydrated) {
		return children;
	}

	// TEMP: observed odd React behavior during two-stage hydration. Apparently it doesn't care about interim state
	// of the component upon hydration as long as it is *synchronously* rendered, so empty HTML makes no difference
	// and doesn't cause blank flash. So less work is better in this case. However if hydration fails and React
	// falls back to client rendering the blank flash occurs, so `innerHTML` comes handy here.
	const innerHTML = fullCycleOutput as string;

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={style}
			data-testid="two-stage-hydration"
			data-fullcycle-name={name}
			// eslint-disable-next-line react/no-danger
			dangerouslySetInnerHTML={{ __html: innerHTML }}
		/>
	);
};

export const TwoStageHydration = ({
	name,
	style,
	children,
}: {
	name: string;
	style?: React.CSSProperties;
	children: React.ReactNode;
}) => {
	return (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		<TwoStageHydrationInner
			name={name}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={style}
			enabled={expValEquals('confluence_editor_hydration_partial', 'isEnabled', true)}
		>
			{children}
		</TwoStageHydrationInner>
	);
};
